import map from 'lodash/map';
import get from 'lodash/get';
import each from 'lodash/each';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import startCase from 'lodash/startCase';
import find from 'lodash/find';
import toUpper from 'lodash/toUpper';
import User from 'yoda-site-components/lib/helpers/User/User';
import { toCapitalize } from 'yoda-site-components/lib/helpers/Utils/utils';
import { imagePath, isGiftCardOrder } from 'yoda-checkout-components/lib/helpers/Utils/Utils';
import {
    CREDIT_CARD_FORM_CONFIG_TOOLTIP_MSG,
    CREDIT_CARD_FORM_CONFIG_TOOLTIP_JCP_CVV_MSG,
    JCP,
    JCPENNEY_MASTERCARD,
} from 'yoda-checkout-components/lib/common/Constants';
import {
    CARDNUMBERRENTRYREQUIRED_AUTH,
    UNVERIFIED_AUTH,
} from 'yoda-checkout-components/lib/common/LabelConstants';
import { normalizePhoneNumber } from 'yoda-account-components/lib/helpers/PhoneNumberFormatter/PhoneNumberFormatter';
import { detectisIE } from 'yoda-core-components/lib/helpers/Utils/Utils';
import PaymentConstants from '../helpers/PaymentConstants';
import ShippingService from './ShippingService';
import { getCreditCardType, getPreferredPaymentCard } from '../helpers/Utils/Utils';

/**
 * Return list of optional month based on selected year
 * @param expYear selected year
 * @return Array [{value: 1}, {value: 2}, ....]
 */
const getOptionMonthList = (expYear) => {
    let monthRange = 12;
    let startMonth = 1;
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear().toString();
    const currentMonth = currentDate.getMonth();
    if (expYear && expYear === currentYear) {
        monthRange = 12 - currentMonth;
        startMonth = currentMonth + startMonth;
    }
    const monthList = Array.from(Array(monthRange), (x, i) => {
        const month = startMonth + i;
        return {
            key: month,
            value: month,
        };
    });
    return monthList;
};

/**
 * Return special char
 * @param str
 * @return {boolean}
 */
const isSpecialCharExist = (str) => {
    const specialCharsFormat = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
    return specialCharsFormat.test(str);
};

/**
 * Return list of optional year
 * @param length of year list
 * @return Array [{key: 2019, value: 2019}, ....]
 */
const getOptionYearList = (maxYear = 20) => {
    const currentYear = new Date().getFullYear();
    const yearList = Array.from(Array(maxYear), (x, i) => {
        const year = currentYear + i;
        return {
            key: year,
            value: year,
        };
    });
    return yearList;
};

/**
 * Return formatted credit card number
 * 1. space between every four digits
 * 2. trim empty spaces
 * @param value
 * @returns {*|string|8888 8888 888}
 */
const formatCreditCard = (value) => {
    // eslint-disable-next-line no-param-reassign
    value = value.replace(/.{4}/g, (match) => `${match} `);
    if (value.endsWith(' ')) {
        // eslint-disable-next-line no-param-reassign
        value = value.substring(0, value.length - 1);
    }
    return value || '';
};

/**
 * Return full card name based on card type
 * @param cardName
 * @returns {string}
 */
const getFormattedCardName = (cardName) => {
    try {
        const cardTypeNameMap = {
            jcp: 'JCP Credit Card',
            jcpmaster: 'JCP Mastercard',
            jcpenneymastercard: 'JCP Mastercard',
            americanexpress: 'American Express',
            amex: 'American Express',
            mastercard: 'Mastercard',
            visa: 'Visa',
            discover: 'Discover',
            giftcard: 'Gift Card',
            bnpl_syf: 'Synchrony Pay Later',
        };
        const lowerCaseName = cardName.toLowerCase();
        return cardTypeNameMap[lowerCaseName.replace(/\s/g, '')] || startCase(lowerCaseName);
    } catch (err) {
        console.log(err);
    }
    return '';
};

/**
 * Return full card name based on card type
 * @param cardType
 * @returns {string}
 */
const getFullCardName = (cardType, options) => {
    let cardFullName = getFormattedCardName(cardType);
    if (options && options.cardNo) {
        const lastFourDigit = options.cardNo.replace(/\*/gi, '');
        cardFullName = `${cardFullName} ****${lastFourDigit.slice(-4)}`;
    }
    return cardFullName;
};

/**
 * Return card icon name based on card type
 * @param type
 * @returns {string}
 */
const getCardImageUrl = (type) => {
    const typeImage = type.toLowerCase();
    const cardIcon = PaymentConstants.cardImageMap[typeImage].imageName;
    return cardIcon;
};

const getCardIconList = (context, type) => {
    const isIE = detectisIE();

    const jcp = imagePath(context, PaymentConstants.JCP_CARD_ICON_NAME);
    const dual = imagePath(context, PaymentConstants.DUAL_CARD_ICON_NAME);
    const visa = imagePath(context, PaymentConstants.VISA_CARD_ICON_NAME);
    const master = imagePath(context, PaymentConstants.MASTER_CARD_ICON_NAME);
    const discover = imagePath(context, PaymentConstants.DISCOVER_CARD_ICON_NAME);
    const amex = imagePath(context, PaymentConstants.AMEX_CARD_ICON_NAME);
    const jcpDim = imagePath(context, PaymentConstants.JCP_CARD_DIM_ICON_NAME);
    const dualDim = imagePath(context, PaymentConstants.DUAL_CARD_DIM_ICON_NAME);
    const visaDim = imagePath(context, PaymentConstants.VISA_CARD_DIM_ICON_NAME);
    const masterDim = imagePath(context, PaymentConstants.MASTER_CARD_DIM_ICON_NAME);
    const discoverDim = imagePath(context, PaymentConstants.DISCOVER_CARD_DIM_ICON_NAME);
    const amexDim = imagePath(context, PaymentConstants.AMEX_CARD_DIM_ICON_NAME);

    if (!type) {
        const iconactiveList = {
            jcpCardImage: {
                image: jcp,
            },
            dualCardImage: {
                image: dual,
            },
            visaCardImage: {
                image: visa,
            },
            masterCardImage: {
                image: master,
            },
            discoverCardImage: {
                image: discover,
            },
            amexCardImage: {
                image: amex,
            },
        };
        return iconactiveList;
    }
    const iconInactiveList = {
        jcpCardImage: {
            image: isIE ? jcpDim : jcp,
            isDim: true,
        },
        dualCardImage: {
            image: isIE ? dualDim : dual,
            isDim: true,
        },
        visaCardImage: {
            image: isIE ? visaDim : visa,
            isDim: true,
        },
        masterCardImage: {
            image: isIE ? masterDim : master,
            isDim: true,
        },
        discoverCardImage: {
            image: isIE ? discoverDim : discover,
            isDim: true,
        },
        amexCardImage: {
            image: isIE ? amexDim : amex,
            isDim: true,
        },
    };
    iconInactiveList[PaymentConstants.cardImageMap[type].key] = {
        image: imagePath(context, PaymentConstants.cardImageMap[type].imageName),
    };
    return iconInactiveList;
};

/**
 * Return credit card UI map based on draft-order billing API
 * @param selectedCardInfo
 * @param orderBillingInfo
 * @param context
 * @returns {{expMonthOptions: *, expYearOptions: *, imageURL}}
 */
const getCreditCardUIMapFromAPI = (selectedCardInfo = {}, context) => {
    // NOSONAR
    const { expiryYear } = selectedCardInfo;
    const cardInfo = {
        expMonthOptions: getOptionMonthList(expiryYear),
        expYearOptions: getOptionYearList(),
        imageURL: imagePath(context, PaymentConstants.DEFAULT_CARD_ICON_NAME),
        IconList: getCardIconList(context),
        isNumberEditable: true,
        isNortonSecurityBadge: get(context, 'featureFlags.enableNortonSecurityBadge', true),
    };
    let isUserLoggedIn = User.isUserLoggedIn();
    if (__SERVER__) {
        isUserLoggedIn = get(context, 'headers.cookies.DP_USER_STATE') === '1';
    }
    if (isUserLoggedIn) {
        cardInfo.saveCardToProfile = {
            show: true,
            selected: false,
        };
        cardInfo.showExpressCheckout = true;
    }
    if (selectedCardInfo.id) {
        let updatedSelectedCardInfo = selectedCardInfo;
        if (isUserLoggedIn && selectedCardInfo.transientPayment) {
            updatedSelectedCardInfo = selectedCardInfo.orderPaymentInfo;
            cardInfo.isNumberEditable = false;
        }
        cardInfo.id = updatedSelectedCardInfo.id;
        cardInfo.number = updatedSelectedCardInfo.number;
        cardInfo.expMonth = updatedSelectedCardInfo.expiryMonth;
        cardInfo.expYear = updatedSelectedCardInfo.expiryYear;
        cardInfo.cvv = '';
        cardInfo.type = updatedSelectedCardInfo.type.toLowerCase();
        cardInfo.imageURL = imagePath(
            context,
            PaymentConstants.cardImageMap[cardInfo.type].imageName
        );
        cardInfo.IconList = getCardIconList(context, cardInfo.type.toLowerCase());
        cardInfo.helpIconMsg = CREDIT_CARD_FORM_CONFIG_TOOLTIP_MSG;
        cardInfo.cardPLCCCVVImageURL = '';
        if (cardInfo.type === 'jcp') {
            cardInfo.cardPLCCCVVImageURL = imagePath(context, PaymentConstants.PLCC_ILLUCSTRATION);
            cardInfo.helpIconMsg = CREDIT_CARD_FORM_CONFIG_TOOLTIP_JCP_CVV_MSG;
        }
        if (isUserLoggedIn) {
            // Contract is mismatched from account and order API.
            // So, reading cc number, expired month and expired year from account
            cardInfo.showExpressCheckout = !selectedCardInfo.transientPayment;
            if (!selectedCardInfo.transientPayment) {
                const paymentSource = get(selectedCardInfo, 'paymentSource', {});
                cardInfo.isNumberEditable = false;
                cardInfo.number = paymentSource.cardNumber;
                cardInfo.expMonth = paymentSource.expiryMonth;
                cardInfo.expYear = paymentSource.expiryYear;
                cardInfo.address = paymentSource.address;
                if (
                    selectedCardInfo.status ===
                    PaymentConstants.CREDIT_CARD_NUMBER_REENTRY_REQUIRED_TYPE
                ) {
                    cardInfo.isITRM = true;
                    cardInfo.isNumberEditable = true;
                    cardInfo.number = '';
                    cardInfo.expMonth = '';
                    cardInfo.expYear = '';
                    cardInfo.type = '';
                    cardInfo.showExpressCheckout = false;
                    const cardInfoNumber = (selectedCardInfo.cardName || '').trim().slice(-4);
                    cardInfo.placeholder = `Re-enter card ending in ${cardInfoNumber}`;
                    cardInfo.authMessageInfo = {
                        title: CARDNUMBERRENTRYREQUIRED_AUTH.TITLE,
                        content: CARDNUMBERRENTRYREQUIRED_AUTH.MESSAGE,
                    };
                } else if (
                    selectedCardInfo.status &&
                    selectedCardInfo.status.toUpperCase() === PaymentConstants.UNVERIFIED_CARD_TYPE
                ) {
                    cardInfo.cvvReentryRequired = true;
                    cardInfo.showExpressCheckout = false;
                    cardInfo.authMessageInfo = {
                        title: UNVERIFIED_AUTH.TITLE,
                        content: UNVERIFIED_AUTH.MESSAGE.CVVAuthFailMessage,
                    };
                }
                cardInfo.isExpressCheckout = paymentSource.expressCheckout;
            }
            cardInfo.saveCardToProfile = {
                show: false,
                selected: false,
            };
            cardInfo.transientPayment = selectedCardInfo.transientPayment;
        }
        const cardNameOptions = {
            cardNo: cardInfo.number,
        };
        cardInfo.cardName = `${getFullCardName(cardInfo.type, cardNameOptions)} `;
    }
    return cardInfo;
};

/**
 * Return credit card UI map based on user input value
 * @param attribute
 * @param value
 * @param options
 */
const getCreditCardUIMapFromUserInput = (attribute, value, options) => {
    const cardInfo = {};
    if (attribute === 'number') {
        let notFormattedCardNumber = value.replace(/\D+/g, '');
        if (isSpecialCharExist(value)) {
            notFormattedCardNumber = '';
            const oldValue = get(options, 'oldValue', '');
            const lastUpdatedChar = value.charAt(value.length - 1);
            if (
                oldValue.length < value.length &&
                !isSpecialCharExist(lastUpdatedChar) &&
                // eslint-disable-next-line no-restricted-globals
                !isNaN(lastUpdatedChar)
            ) {
                notFormattedCardNumber = lastUpdatedChar;
            }
        }
        let cardTypeEnum = getCreditCardType(notFormattedCardNumber, options.context);
        const ccMaxLength =
            (cardTypeEnum || {}).name === 'amex'
                ? PaymentConstants.AMEX_CREDIT_CARD_MAX_LENGTH
                : PaymentConstants.CREDIT_CARD_MAX_LENGTH;
        if (notFormattedCardNumber.length > ccMaxLength) {
            notFormattedCardNumber = notFormattedCardNumber.substring(0, ccMaxLength);
        }
        cardTypeEnum = getCreditCardType(notFormattedCardNumber, options.context);
        let cardIcon = PaymentConstants.DEFAULT_CARD_ICON_NAME;
        /*
         * Reset expMonth, expYear and CVV if card type is changed
         */
        if (cardTypeEnum && cardTypeEnum.name !== options.type) {
            cardInfo.expMonth = '';
            cardInfo.expYear = '';
            cardInfo.cvv = '';
            cardInfo.expMonthOptions = getOptionMonthList();
        }
        /*
         * Update the card type based on card number
         */
        cardInfo.type = cardTypeEnum && cardTypeEnum.name;
        if (cardInfo.type) {
            cardIcon = PaymentConstants.cardImageMap[cardInfo.type.toLowerCase()].imageName;
            cardInfo.helpIconMsg = CREDIT_CARD_FORM_CONFIG_TOOLTIP_MSG;
            cardInfo.IconList = getCardIconList(options.context, cardInfo.type.toLowerCase());
            cardInfo.cardPLCCCVVImageURL = '';
            if (cardInfo.type === 'jcp') {
                cardInfo.cardPLCCCVVImageURL = imagePath(
                    options.context,
                    PaymentConstants.PLCC_ILLUCSTRATION
                );
                cardInfo.helpIconMsg = CREDIT_CARD_FORM_CONFIG_TOOLTIP_JCP_CVV_MSG;
            }
        } else {
            cardInfo.IconList = getCardIconList(options.context);
        }
        // eslint-disable-next-line no-param-reassign
        value = formatCreditCard(notFormattedCardNumber);
        cardInfo.imageURL = imagePath(options.context, cardIcon);
    } else if (attribute === 'cvv') {
        // eslint-disable-next-line no-param-reassign
        value = value.replace(/\D+/g, '');
    } else if (attribute === 'expYear') {
        /*
         * Reset expMonth if expYear is current year and selected expMonth is past
         */
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear().toString();
        const currentMonth = currentDate.getMonth() + 1;
        let selectedMonth = get(options, 'cardInfo.expMonth', '0');
        selectedMonth = parseInt(selectedMonth, 10);
        if (value === currentYear && selectedMonth < currentMonth) {
            cardInfo.expMonth = '';
        }
        cardInfo.expMonthOptions = getOptionMonthList(value);
    }
    cardInfo[attribute] = value;
    return cardInfo;
};

const getUserPaymentListUIMapFromAPI = (paymentFromProfile, orderPaymentInfo, options) => {
    const userPaymentList = [];
    const orderPaymentId = get(orderPaymentInfo, 'id');
    const orderAccountPaymentMethodId = get(
        orderPaymentInfo,
        'relationships.accountPaymentMethod.id',
        null
    );
    /**
     * Credit card info on order, but not saved to profile
     * Excludes Buy Now Pay Later virtual card info
     */
    const isTransientPayment =
        !isEmpty(orderPaymentId) &&
        !orderAccountPaymentMethodId &&
        orderPaymentInfo.authType !== 'PARTIAL_AUTH' &&
        orderPaymentInfo.subtype !== 'BNPL_SYF';
    const userPaymentTypeMap = {};
    userPaymentTypeMap['American Express'] = 'amex';
    userPaymentTypeMap['Master Card'] = 'mastercard';
    map(paymentFromProfile, (paymentSource) => {
        const cardType = userPaymentTypeMap[paymentSource.type]
            ? userPaymentTypeMap[paymentSource.type]
            : paymentSource.type;
        const cardIcon = getCardImageUrl(cardType);
        const paymentInfo = {
            paymentSource: { ...paymentSource },
        };
        /**
         * Order API Billing address "addressLineOne & addressLineTwo" not match with Account API Payment List address "lineone & linetwo"
         */
        paymentInfo.paymentSource.address.lineOne =
            paymentInfo.paymentSource.address.addressLineOne;
        paymentInfo.paymentSource.address.lineTwo =
            paymentInfo.paymentSource.address.addressLineTwo;
        const cardNameOptions = {
            cardNo: paymentSource.cardNumber,
        };
        paymentInfo.id = paymentSource.id;
        paymentInfo.userName = `${paymentSource.address.firstName} ${paymentSource.address.lastName}`;
        paymentInfo.cardName = `${getFullCardName(paymentSource.type, cardNameOptions)} `;
        paymentInfo.type = cardType;
        paymentInfo.expiryYear = paymentSource.expiryYear && paymentSource.expiryYear.toString();
        paymentInfo.expiryMonth = paymentSource.expiryMonth && paymentSource.expiryMonth.toString();
        paymentInfo.showDefaultLabel = paymentSource.expressCheckout;
        paymentInfo.imageUrl = imagePath(options.context, cardIcon);
        paymentInfo.IconList = getCardIconList(options.context, cardType.toLowerCase());
        paymentInfo.helpIconMsg = CREDIT_CARD_FORM_CONFIG_TOOLTIP_MSG;
        paymentInfo.cardPLCCCVVImageURL = '';
        if (paymentInfo.type === 'jcp') {
            paymentInfo.cardPLCCCVVImageURL = imagePath(
                options.context,
                PaymentConstants.PLCC_ILLUCSTRATION
            );
            paymentInfo.helpIconMsg = CREDIT_CARD_FORM_CONFIG_TOOLTIP_JCP_CVV_MSG;
        }
        paymentInfo.showDeleteLink = paymentSource.expressCheckout;
        paymentInfo.status = paymentSource.status;
        paymentInfo.transientPayment = false;
        /**
         * Invoke user change the payment method
         */
        if (options.selectedPaymentId) {
            paymentInfo.selected = paymentSource.id === options.selectedPaymentId;
        } else if (isEmpty(orderPaymentInfo)) {
            paymentInfo.selected = paymentSource.expressCheckout;
        } else {
            paymentInfo.selected = paymentSource.id === orderAccountPaymentMethodId;
        }

        if (paymentSource.status === PaymentConstants.CREDIT_CARD_NUMBER_REENTRY_REQUIRED_TYPE) {
            paymentInfo.isITRM = true;
        } else if (paymentSource.status.toUpperCase() === PaymentConstants.UNVERIFIED_CARD_TYPE) {
            paymentInfo.cvvReentryRequired = true;
        }
        paymentInfo.isPromoFinanceApplied = false;
        const jcpCardTypes = ['jcp', 'jcpenneymastercard'];
        if (options.isPromoFinanceApplied && jcpCardTypes.indexOf(paymentInfo.type) > -1) {
            paymentInfo.isPromoFinanceApplied = true;
        }
        userPaymentList.push(paymentInfo);
    });
    if (isTransientPayment) {
        const paymentInfo = {
            orderPaymentInfo,
            transientPayment: true,
        };
        const cardNameOptions = {
            cardNo: orderPaymentInfo.number,
        };
        const cardIcon = getCardImageUrl(orderPaymentInfo.type);
        paymentInfo.id = orderPaymentInfo.id;
        paymentInfo.userName = `${orderPaymentInfo.address.firstName} ${orderPaymentInfo.address.lastName}`;
        paymentInfo.cardName = `${getFullCardName(orderPaymentInfo.type, cardNameOptions)} `;
        paymentInfo.type = orderPaymentInfo.type;
        paymentInfo.expiryYear =
            orderPaymentInfo.expiryYear && orderPaymentInfo.expiryYear.toString();
        paymentInfo.expiryMonth =
            orderPaymentInfo.expiryMonth && orderPaymentInfo.expiryMonth.toString();
        paymentInfo.showDefaultLabel = false;
        paymentInfo.imageUrl = imagePath(options.context, cardIcon);
        paymentInfo.IconList = getCardIconList(
            options.context,
            orderPaymentInfo.type.toLowerCase()
        );
        paymentInfo.helpIconMsg = CREDIT_CARD_FORM_CONFIG_TOOLTIP_MSG;
        paymentInfo.cardPLCCCVVImageURL = '';
        if (orderPaymentInfo.type === 'jcp') {
            paymentInfo.cardPLCCCVVImageURL = imagePath(
                options.context,
                PaymentConstants.PLCC_ILLUCSTRATION
            );
            paymentInfo.helpIconMsg = CREDIT_CARD_FORM_CONFIG_TOOLTIP_JCP_CVV_MSG;
        }
        paymentInfo.showDeleteLink = false;
        paymentInfo.selected = true;
        if (options.selectedPaymentId) {
            paymentInfo.selected = paymentInfo.id === options.selectedPaymentId;
        }
        paymentInfo.status = orderPaymentInfo.status;
        userPaymentList.push(paymentInfo);
    }
    const selectedPayment = find(userPaymentList, { selected: true });
    const paymentCardIndex = getPreferredPaymentCard(userPaymentList);
    if (!selectedPayment && userPaymentList.length > 0) {
        userPaymentList[paymentCardIndex].selected = true;
    }
    /*
     * Sorting selected payment as first
     */
    sortBy(userPaymentList, { selected: false });
    return userPaymentList;
};

/**
 * Return email id from order
 * @param orderDetailsInfo
 * @returns emailId
 */
const getEmailIdFromOrder = (orderDetailsInfo) => {
    const orderContectMethod = get(orderDetailsInfo, 'data.contact.methods', []);
    const emailMethod = find(orderContectMethod, { type: 'EMAIL' }) || {};
    return emailMethod.value;
};

/**
 * Return true if order total amount is covered with added gift card else return false
 * @param orderDetailsInfo
 */
const isPartialGiftcardPayment = (orderDetailsInfo) => {
    const orderTotalList = get(orderDetailsInfo, 'data.totals', []);
    const totalBalance = find(orderTotalList, { type: 'BALANCE' }) || {};
    return totalBalance.amount !== 0;
};

/**
 * Return order total
 * @param getOrderTotal
 */
const getOrderTotal = (orderDetailsInfo) => {
    const orderTotalList = get(orderDetailsInfo, 'data.totals', []);
    const orderAmount = find(orderTotalList, { type: 'ORDER AMOUNT' }) || {};
    return orderAmount.amount ? orderAmount.amount : 0;
};

/**
 * Show Paypal button if feature enabled and price is accurate and no gift card item and no full GC payment
 * @param orderDetailsInfo
 * @returns {boolean}
 */
const isPaypalEligible = (orderDetailsInfo, options) => {
    const isPriceAccurate = get(orderDetailsInfo, 'data.isPriceAccurate', false);
    const showNewCreditCardForm = isPartialGiftcardPayment(orderDetailsInfo);
    const bagItems = get(orderDetailsInfo, 'data.bag.items', []);
    const isOrderHasGiftCardItem = isGiftCardOrder(bagItems);
    const isPayPalEligibleFromHeader = get(orderDetailsInfo, 'isPayPalStrategiesAvailable', false);
    const { paypal = true } = options?.context?.featureFlags || {};
    const payPalEligible =
        isPayPalEligibleFromHeader &&
        paypal &&
        isPriceAccurate &&
        !isOrderHasGiftCardItem &&
        showNewCreditCardForm;
    return payPalEligible;
};

/**
 * Return true if paypal payment added into order else return false
 * @param orderDetailsInfo
 */
const isPaypalPaymentAdded = (orderDetailsInfo) => {
    const isPaypal = get(orderDetailsInfo, 'data.billing.paypal');
    return !!isPaypal;
};

const isPromoFinanceApplied = (orderDetailsInfo) => {
    let isPromoFinance = get(orderDetailsInfo, 'data.billing.creditCards.financing.options');
    if (!isPromoFinance) {
        isPromoFinance = get(
            orderDetailsInfo,
            'data.billing.creditCards.cards[0].financing.options'
        );
    }
    return !!isPromoFinance;
};

/**
 * Update user selected payment in payment list
 * @param {String} selectedPaymentId
 * @param {Array of payment} paymentList
 */
const selectedPaymentUpdateInPaymentList = (selectedPaymentId, paymentList) => {
    const updatedUserPaymentList = [];
    map(paymentList, (payment) => {
        const updatedUserPayment = { ...payment };
        updatedUserPayment.selected = false;
        if (selectedPaymentId === updatedUserPayment.id) {
            updatedUserPayment.selected = true;
        }
        updatedUserPaymentList.push(updatedUserPayment);
    });
    return updatedUserPaymentList;
};
/**
 * Edit Guest or transient Payment Payload Constract
 * @param {*} cardId
 * @param {*} payload
 */
const guestBillingPayload = (cardId, paymentInfo, errorHandler, updatedBillingAddress) => {
    const billingAddress =
        updatedBillingAddress || get(paymentInfo, 'creditCardUserInputInfo.billingAddress', {});
    const cardInfo = get(paymentInfo, 'creditCardUserInputInfo.cardInfo', {});
    const notFormattedPhoneNumber = billingAddress.phone.replace(/\D+/g, '');
    const billingAddressLineTwo = billingAddress.lineTwo || billingAddress.addressLineTwo;
    const billingAddressPayload = {
        city: billingAddress.city,
        country: billingAddress.country,
        firstName: billingAddress.firstName,
        lastName: billingAddress.lastName,
        lineOne: billingAddress.lineOne || billingAddress.addressLineOne,
        state: billingAddress.state,
        zip: billingAddress.zip,
        phone: notFormattedPhoneNumber,
    };

    billingAddressLineTwo && (billingAddressPayload.lineTwo = billingAddressLineTwo);
    billingAddress.company && (billingAddressPayload.company = billingAddress.company);
    billingAddress.secondaryPhone &&
        (billingAddressPayload.secondaryPhone = billingAddress.secondaryPhone);
    const USCountries = ['APO', 'FPO', 'DPO'];
    if (billingAddress.country !== 'US' && !USCountries.includes(billingAddress.country)) {
        billingAddressPayload.province = billingAddress.state;
    }
    if (USCountries.includes(billingAddress.country)) {
        billingAddressPayload.city = billingAddress.country;
        billingAddressPayload.country = 'AP';
        billingAddressPayload.militaryAddressType = billingAddress.country;
    }
    /*
     * MCYODA-26243 => Country code label (Country or APO/FPO/DPO) is passed to downstream instead of country code
     * This is a defensive fix for sometimes the label (Country or APO/FPO/DPO) is getting auto-selected in the
     * drop-down section in mobile devices
     * */
    /** MCYODA-30179 - country code sending as select in firefox - defensive fix */

    if (
        billingAddressPayload.country.includes('Country') ||
        billingAddressPayload.country?.indexOf('Select') > -1
    ) {
        billingAddressPayload.country = 'US';
    }
    const billingPayload = {
        payload: {
            address: billingAddressPayload,
            type: cardInfo.type,
            cvv: cardInfo.cvv,
            errorHandler,
        },
    };
    if (cardInfo.type !== 'jcp') {
        billingPayload.payload.expiryMonth = cardInfo.expMonth;
        billingPayload.payload.expiryYear = cardInfo.expYear;
    }
    /*
     * Set address standardization for billing address
     */
    if (!updatedBillingAddress || paymentInfo.xStandardizationNotRequired) {
        billingPayload.payload.isXStandardizationRequired = 'false';
    }
    return billingPayload;
};

/**
 * Return errorCode if selected Payment method or default Payment method
 *
 */
const getITRMErrors = () => {
    const ITRMErrorCode = {
        number: 'Enter a credit card number',
        cvv: 'Please enter a valid card verification number',
        expMonth: 'Select a valid month',
        expYear: 'Select a valid year',
    };
    return ITRMErrorCode;
};

/**
 * Return errorDescription & constract DA error construct format to capture.
 *
 */
const getDAPaymentFormError = (formErrors) => {
    const DAFormErrors = [];
    const keys = Object.keys(formErrors);
    each(keys, (key) => {
        DAFormErrors.push({ errorDescription: formErrors[key] });
    });
    return DAFormErrors;
};

/**
 * Check if billing and shipping address are same or not
 * @param orderDetailsInfo
 * @return {boolean}
 */
const isShippingBillingAddressSame = (orderDetailsInfo) => {
    const orderShippingList = get(orderDetailsInfo, 'data.delivery.groups', []);
    const orderShipToHomeInfo = find(orderShippingList, { type: 'HOME' }) || {};
    const shippingAddress = orderShipToHomeInfo.address;
    if (isEmpty(shippingAddress)) {
        return false;
    }
    let billingAddress = get(orderDetailsInfo, 'data.billing.creditCards.cards[0].address', {});
    if (isEmpty(billingAddress)) {
        billingAddress = get(orderDetailsInfo, 'data.billing.unnamed.address', {});
    }
    if (isEmpty(billingAddress)) {
        return true;
    }
    const addressFields = [
        'firstName',
        'lastName',
        'company',
        'lineOne',
        'city',
        'zip',
        'state',
        'country',
    ];
    let isShippingBillingSame = true;
    each(addressFields, (key) => {
        if (shippingAddress[key] !== billingAddress[key]) {
            isShippingBillingSame = false;
        }
    });
    return isShippingBillingSame;
};

/**
 * Return billing address UI as per mock
 * orderBillingInfo: {
 *      name: 'fistName lastName',
 *      addressLineOne: 'lineOne, lineTwo',
 *      addressLineTwo: 'city, state zip' or ' city, state'
 *      addressLineThree: 'international_country zip'
 *      phone: 'register user only or Paypal address added'
 * }
 * @param address
 */
const getOrderBillingInfoUI = (address, isPaypalAddressAdded, isUserLoggedInSSR) => {
    // added or condition because CAM api will respond back as addressLineOne
    const lineOne = address.lineOne || address.addressLineOne;
    const lineTwo = address.lineTwo || address.addressLineTwo;
    const orderBillingInfoUI = {
        name: `${address.firstName} ${address.lastName}`,
        company: address.company,
        addressLineOne: lineTwo ? `${lineOne}, ${lineTwo}` : lineOne,
        addressLineTwo: `${toCapitalize(address.city)}, ${address.state} ${address.zip}`,
        phone: address.phone,
    };
    orderBillingInfoUI.name = toCapitalize(orderBillingInfoUI.name);
    orderBillingInfoUI.addressLineOne = toCapitalize(orderBillingInfoUI.addressLineOne);
    orderBillingInfoUI.company = toCapitalize(orderBillingInfoUI.company);
    if (address.country !== 'US') {
        const city = address.militaryAddressType || address.city;
        if (ShippingService.isMilitaryAddress(city)) {
            orderBillingInfoUI.addressLineTwo = `${city}, ${address.state} ${address.zip}`;
        } else {
            orderBillingInfoUI.addressLineTwo = `${toCapitalize(address.city)}, ${toCapitalize(
                address.state
            )}`;
            orderBillingInfoUI.addressLineThree = `${toCapitalize(address.country)} ${address.zip}`;
        }
    }
    let isUserLoggedIn = User.isUserLoggedIn();
    if (__SERVER__) {
        isUserLoggedIn = isUserLoggedInSSR;
    }
    if (isUserLoggedIn || isPaypalAddressAdded) {
        orderBillingInfoUI.phone = isPaypalAddressAdded
            ? address.phone
            : normalizePhoneNumber()(address.phone) || address.phone;
    }
    orderBillingInfoUI.source = address;
    return orderBillingInfoUI;
};

const deleteSavedPaymentCC = (userPaymentList = [], deletedCreditCardId) => {
    let result = userPaymentList;
    if (userPaymentList.length > 0 && deletedCreditCardId) {
        result = userPaymentList.filter(
            (item) => !(item.id === deletedCreditCardId && item.transientPayment)
        );
    }
    return result;
};

const isJcpCardInOrder = (cards) =>
    !isEmpty(
        cards.find(
            (card) => toUpper(card.type) === JCP || toUpper(card.type) === JCPENNEY_MASTERCARD
        )
    );

export default {
    getCreditCardUIMapFromAPI,
    getCreditCardUIMapFromUserInput,
    getUserPaymentListUIMapFromAPI,
    getEmailIdFromOrder,
    isPaypalEligible,
    isPaypalPaymentAdded,
    isPartialGiftcardPayment,
    selectedPaymentUpdateInPaymentList,
    guestBillingPayload,
    getITRMErrors,
    getDAPaymentFormError,
    isShippingBillingAddressSame,
    getOrderBillingInfoUI,
    isPromoFinanceApplied,
    isJcpCardInOrder,
    getFormattedCardName,
    getOrderTotal,
    deleteSavedPaymentCC,
};
